import type { UniBoxApi, UniBoxDataset, UniBoxEntity, UniBoxSequenceStep } from './types';
import type { MappedLeadData, OutreachStep } from '../../types';

/**
 * DUMMY DATA — intentional source of truth for THIS phase.
 *
 * Every lead below uses the agreed three-flat-column model:
 *   status    — global enum key (see taxonomy.ts): leads|enriched|outreaching|
 *               outreached|engaged|proposal|converted|discarded
 *   substatus — per-module substatus key (Sales vocab, see taxonomy.ts)
 *   outcome   — orthogonal reason/result column (or null)
 *
 * Plus `sequenceProgress`/`sequenceSteps` per channel for outreaching leads,
 * mirroring the shape the real `extractOutreachSequences` fix now produces
 * (each step carries sentAt). Next phase this whole file is replaced by the
 * backend mapping — by design, not speculation.
 */

const seq = (
  channel: string,
  total: number,
  sent: number,
  bodies: string[],
  subject?: string,
): { progress: UniBoxEntity['sequenceProgress']; steps: UniBoxSequenceStep[] } => {
  const steps: UniBoxSequenceStep[] = Array.from({ length: total }, (_, i) => {
    const stepNum = i + 1;
    const isSent = stepNum <= sent;
    return {
      step: stepNum,
      channel,
      subject: subject && stepNum === 1 ? subject : undefined,
      body: bodies[i] || '',
      // Pretend earlier steps were sent over the prior days.
      sentAt: isSent ? `2026-06-${String(28 - (total - stepNum)).padStart(2, '0')}T10:00:00Z` : null,
    };
  });
  return {
    progress: [{ channel, total, sent, nextStep: sent >= total ? null : sent + 1 }],
    steps,
  };
};

export const salesDataset: UniBoxDataset = {
  feed: {
    campaigns: [
      { id: '869cp02nw', name: 'PGRMT Q3 Outbound', automation_mode: 'autopilot', total: 6, needs_human: 1, status: 'active', start: '2026-06-15', done: '2026-09-15', created: '2026-06-10', search_location: 'DACH Region (DE/AT/CH)', seller_lvl1: 'seller-ingrid-chen', seller_name: 'Ingrid Chen' },
      { id: '471xk88za', name: 'Nordic SaaS Launch', automation_mode: 'copilot', total: 5, needs_human: 0, status: 'paused', start: '2026-06-20', done: null, created: '2026-06-18', search_location: 'Nordics (SE/NO/DK/FI)', seller_lvl1: 'seller-nick-garcia', seller_name: 'Nick Garcia' },
    ],
    leads: [
      {
        lvl1_id: 35596,
        lvl2_id: 70412,
        client_name: 'Sofia R.',
        company: 'NEXUS Labs',
        avatar: null,
        campaign_id: '869cp02nw',
        status: 'engaged',
        substatus: 'qualified',
        outcome: null,
        control_state: 'ai_running',
        escalation_reason: null,
        timezone: 'Europe/Madrid',
        channels: ['whatsapp', 'email'],
        last_message_snippet: 'The liquid desktop is beautiful',
        last_activity_at: '2026-07-01T09:12:00Z',
        needs_reply: false,
        next_send: null,
        // AI-assigned opportunity value (qualified). Currency inherits the team
        // currency (EUR) — omitted here to exercise the fallback. TODO(backend).
        opportunity_value: 48000,
      },
      {
        lvl1_id: 35597,
        lvl2_id: 70413,
        client_name: 'FABEL Team',
        company: 'Fabel',
        avatar: null,
        campaign_id: '869cp02nw',
        status: 'engaged',
        substatus: 'objection',
        outcome: null,
        control_state: 'escalated',
        escalation_reason: 'objection_detected',
        timezone: 'America/New_York',
        channels: ['email'],
        last_message_snippet: 'Not sure the pricing works for us...',
        last_activity_at: '2026-07-01T09:00:00Z',
        needs_reply: true,
        next_send: null,
      },
      {
        lvl1_id: 35598,
        lvl2_id: 70414,
        client_name: 'Alex M.',
        company: 'Meridian',
        avatar: null,
        campaign_id: '471xk88za',
        status: 'engaged',
        substatus: 'meeting_requested',
        outcome: null,
        control_state: 'human_takeover',
        escalation_reason: null,
        timezone: 'America/Los_Angeles',
        channels: ['linkedin', 'whatsapp'],
        last_message_snippet: 'The meeting got moved to 3pm.',
        last_activity_at: '2026-07-01T08:15:00Z',
        needs_reply: false,
        next_send: null,
        // Qualified + scored. Carries an explicit currency override (USD).
        opportunity_value: 62500,
        opportunity_currency: 'USD',
      },
      {
        lvl1_id: 35599,
        lvl2_id: 70415,
        client_name: 'Kenji T.',
        company: 'Sentinel',
        avatar: null,
        campaign_id: '471xk88za',
        status: 'outreaching',
        substatus: 'icebreaker',
        outcome: 'waiting',
        control_state: 'ai_running',
        escalation_reason: null,
        timezone: 'Asia/Tokyo',
        channels: ['linkedin'],
        last_message_snippet: '',
        last_activity_at: '2026-06-30T17:40:00Z',
        needs_reply: false,
        lead_role: 'VP Platform Engineering',
        industry: 'Cybersecurity',
        next_send: { channel: 'linkedin', sequence_step: 1, scheduled_at: '2026-07-02T10:00:00Z', preview: 'Hi Kenji, saw Sentinel’s move to microservices...' },
        ...seq('linkedin', 3, 0, [
          'Hi Kenji, saw Sentinel’s move to microservices and the incident-spike post-mortem on the Q1 outage. Most platform teams we work with find the gap is observability at the service boundary, not the tools themselves. We built a way to stitch traces across the mesh without instrumentation rewrites. Open to a 15-min look Thursday?',
          'Hi Kenji, following up — happy to share the two-pager on cross-service trace continuity that landed well with the Acme platform team. Worth a quick read?',
          'Kenji, last note from me: we’re running a small roundtable on incident budgets for platform orgs next week. Would value Sentinel’s perspective if you can make it.',
        ]),
      },
      {
        lvl1_id: 35600,
        lvl2_id: 70416,
        client_name: 'Priya S.',
        company: 'Atlas Cloud',
        avatar: null,
        campaign_id: '869cp02nw',
        status: 'outreaching',
        substatus: 'follow_up',
        outcome: 'waiting',
        control_state: 'ai_running',
        escalation_reason: null,
        timezone: 'Asia/Kolkata',
        channels: ['email', 'linkedin'],
        last_message_snippet: '',
        last_activity_at: '2026-06-30T16:18:00Z',
        needs_reply: false,
        lead_role: 'Director of Support Operations',
        industry: 'SaaS',
        next_send: { channel: 'email', sequence_step: 2, scheduled_at: '2026-07-03T11:30:00Z', preview: 'Sharing two workflow ideas for Atlas Cloud support...' },
        ...seq('email', 3, 1, [
          'Hi Priya, noticed Atlas Cloud is scaling support across APAC and the team has been hiring Tier-2 specialists. The hardest part is usually keeping resolution-time flat while the queue grows — we help SaaS support orgs automate the triage layer without losing the human touch on escalations. Would a 15-minute walkthrough this week be useful?',
          'Hi Priya, following up with two concrete workflow ideas for the Atlas Cloud support queue — one for auto-triaging tickets by sentiment, one for surfacing likely escalations before they breach SLA. Both are config-only, no engineering lift on your side. Want me to send the one-pager?',
          'Priya, last check-in — if the timing isn’t right I’ll stop here. If it is, I can hold 20 minutes Thursday or Friday to walk through the SLA-breach workflow live. Either way, thanks for the consideration.',
        ], 'Standardizing support triage at Atlas Cloud'),
        ...seq('linkedin', 3, 1, [
          'Priya, quick note — saw your post on the support-team hiring push at Atlas Cloud. We help SaaS support orgs keep resolution-time flat through the growth curve. Worth a connect?',
          'Priya, following up — I can share a short case study on a similar APAC support scale-up. Useful?',
          'Priya, final note — happy to be a resource on triage automation whenever it’s relevant. No pressure.',
        ]),
      },
      {
        lvl1_id: 35601,
        lvl2_id: 70417,
        client_name: 'Marcus L.',
        company: 'Northline Ops',
        avatar: null,
        campaign_id: '869cp02nw',
        status: 'outreaching',
        substatus: 'last_message',
        outcome: 'waiting',
        control_state: 'ai_running',
        escalation_reason: null,
        timezone: 'Europe/London',
        channels: ['email'],
        last_message_snippet: '',
        last_activity_at: '2026-06-30T14:05:00Z',
        needs_reply: false,
        lead_role: 'Head of Operations',
        industry: 'Logistics',
        next_send: { channel: 'email', sequence_step: 3, scheduled_at: '2026-07-04T08:45:00Z', preview: 'Here is a concise agenda for the workflow review...' },
        ...seq('email', 3, 2, [
          'Hi Marcus, saw Northline Ops’ expansion into last-mile and the hiring push in regional hubs. The piece that usually breaks first is workflow consistency across sites — same SOP, different tooling per hub. We help ops teams standardize without a rip-and-replace. Worth a short call?',
          'Hi Marcus, following up — two quick ideas from similar rollouts: a single exception-log per hub, and a weekly variance review that takes 15 minutes instead of an hour. Want the short write-up?',
          'Marcus, here is a concise agenda for a 20-minute workflow review — 1) where variance enters your process, 2) the two highest-leverage fixes, 3) what a pilot would look like. If next week doesn’t work, I’ll stop here and you have my details.',
        ], 'Northline Ops workflow review'),
      },
      {
        lvl1_id: 35602,
        lvl2_id: 70418,
        client_name: 'Iris V.',
        company: 'Helio Grid',
        avatar: null,
        campaign_id: '869cp02nw',
        status: 'converted',
        substatus: 'onboarding',
        outcome: null,
        control_state: 'ai_running',
        escalation_reason: null,
        timezone: 'America/Sao_Paulo',
        channels: ['whatsapp'],
        last_message_snippet: 'Looks interesting, send the offer.',
        last_activity_at: '2026-06-30T12:10:00Z',
        needs_reply: false,
        next_send: null,
        // Converted — closed value retained. Inherits team currency (EUR).
        opportunity_value: 38500,
      },
      {
        lvl1_id: 35603,
        lvl2_id: 70419,
        client_name: 'Noah B.',
        company: 'Lumen Stack',
        avatar: null,
        campaign_id: '471xk88za',
        status: 'outreaching',
        substatus: 'outreached',
        outcome: 'no_response',
        control_state: 'ai_running',
        escalation_reason: null,
        timezone: 'Australia/Sydney',
        channels: ['linkedin'],
        last_message_snippet: '',
        last_activity_at: '2026-06-29T19:25:00Z',
        needs_reply: false,
        next_send: null,
        ...seq('linkedin', 3, 3, ['Hi Noah, congrats on the Lumen Stack launch.', 'Following up on the launch pilot scope.', 'Last note — happy to share the pilot outline.']),
      },
      {
        lvl1_id: 35604,
        lvl2_id: 70420,
        client_name: 'Eva K.',
        company: 'Nordbyte',
        avatar: null,
        campaign_id: '471xk88za',
        status: 'discarded',
        substatus: 'bad_timing',
        outcome: 'bad_timing',
        control_state: 'ai_running',
        escalation_reason: null,
        timezone: 'Europe/Berlin',
        channels: ['email', 'whatsapp'],
        last_message_snippet: 'Budget review happens next week.',
        last_activity_at: '2026-06-29T15:55:00Z',
        needs_reply: false,
        next_send: null,
      },
      {
        lvl1_id: 35605,
        lvl2_id: 70421,
        client_name: 'Daniel O.',
        company: 'Cobalt',
        avatar: null,
        campaign_id: '869cp02nw',
        status: 'enriched',
        substatus: '',
        outcome: null,
        control_state: 'ai_running',
        escalation_reason: null,
        timezone: 'Europe/Paris',
        channels: ['email'],
        last_message_snippet: '',
        last_activity_at: '2026-06-29T09:00:00Z',
        needs_reply: false,
        next_send: null,
      },
      {
        lvl1_id: 35606,
        lvl2_id: 70422,
        client_name: 'Lena F.',
        company: 'Vertex',
        avatar: null,
        campaign_id: '471xk88za',
        status: 'leads',
        substatus: '',
        outcome: null,
        control_state: 'ai_running',
        escalation_reason: null,
        timezone: 'US/Central',
        channels: ['linkedin'],
        last_message_snippet: '',
        last_activity_at: '2026-06-28T11:00:00Z',
        needs_reply: false,
        next_send: null,
      },
    ],
  },
  threads: {
    '35596_whatsapp': {
      lvl1_id: 35596,
      channel: 'whatsapp',
      control_state: 'ai_running',
      messages: [
        { id: 101, direction: 'outbound', state: 'sent', sent_by: 'ai', sequence_step: 1, body: 'Hey! Did you see the latest NEXUS prototype?', sent_at: '2026-07-01T09:08:00Z' },
        { id: 102, direction: 'inbound', state: 'received', body: 'The liquid desktop is beautiful', received_at: '2026-07-01T09:12:00Z' },
        { id: 103, direction: 'outbound', state: 'sent', sent_by: 'ai', body: 'Right. Want a quick walkthrough this week?', sent_at: '2026-07-01T09:13:00Z' },
      ],
    },
    '35596_email': {
      lvl1_id: 35596,
      channel: 'email',
      control_state: 'ai_running',
      messages: [
        { id: 201, direction: 'outbound', state: 'sent', sent_by: 'ai', sequence_step: 1, body: 'Hi Sofia, sharing the NEXUS prototype deck attached.', sent_at: '2026-06-30T14:00:00Z' },
        { id: 202, direction: 'inbound', state: 'received', body: 'Thanks. I will review and get back to you.', received_at: '2026-06-30T16:20:00Z' },
      ],
    },
    '35597_email': {
      lvl1_id: 35597,
      channel: 'email',
      control_state: 'escalated',
      messages: [
        { id: 301, direction: 'outbound', state: 'sent', sent_by: 'ai', body: "Here's the contract draft for your review.", sent_at: '2026-07-01T08:30:00Z' },
        { id: 302, direction: 'inbound', state: 'received', body: 'Not sure the pricing works for us...', received_at: '2026-07-01T09:00:00Z' },
      ],
    },
    '35598_linkedin': {
      lvl1_id: 35598,
      channel: 'linkedin',
      control_state: 'human_takeover',
      messages: [
        { id: 401, direction: 'outbound', state: 'sent', sent_by: 'ai', body: 'Hi Alex, would love to set up a pilot.', sent_at: '2026-07-01T07:50:00Z' },
        { id: 402, direction: 'inbound', state: 'received', body: 'The meeting got moved to 3pm.', received_at: '2026-07-01T08:15:00Z' },
        { id: 403, direction: 'outbound', state: 'sent', sent_by: 'human', body: 'No problem, 3pm works. See you then.', sent_at: '2026-07-01T08:18:00Z' },
      ],
    },
    '35598_whatsapp': {
      lvl1_id: 35598,
      channel: 'whatsapp',
      control_state: 'human_takeover',
      messages: [
        { id: 411, direction: 'outbound', state: 'sent', sent_by: 'human', body: 'Hi Alex, confirming our 3pm call today.', sent_at: '2026-07-01T08:20:00Z' },
      ],
    },
    '35602_whatsapp': {
      lvl1_id: 35602,
      channel: 'whatsapp',
      control_state: 'ai_running',
      messages: [
        { id: 621, direction: 'inbound', state: 'received', body: 'Looks interesting, send the offer.', received_at: '2026-06-30T12:10:00Z' },
        { id: 622, direction: 'outbound', state: 'sent', sent_by: 'ai', body: 'Sending a concise offer summary for Helio Grid...', sent_at: '2026-06-30T12:40:00Z' },
      ],
    },
  },
  suggestions: {
    '35596_whatsapp': [
      'Perfect. How about a 15-minute walkthrough Thursday morning?',
      "Great to hear. I'll send a couple of time slots for a demo.",
    ],
    '35597_email': [
      "Totally understand. Let's find a package that fits your budget. Can I show you our starter tier?",
      'Happy to revisit pricing. What range were you expecting so I can tailor a proposal?',
    ],
    '35598_linkedin': [
      "Sounds good. I'll send the pilot scope over now.",
      "Great, talk at 3pm. I'll bring the pilot details.",
    ],
  },
};

export const ordersDataset: UniBoxDataset = {
  feed: {
    inboxes: [
      { id: 'shopify_whatsapp', name: 'Shopify WhatsApp', automation_mode: 'autopilot', total: 3, needs_human: 1 },
      { id: 'support_email', name: 'Support Email', automation_mode: 'copilot', total: 2, needs_human: 0 },
    ],
    customers: [
      {
        lvl1_id: 45601,
        client_name: 'Maya Chen',
        company: 'Order #A-1048',
        avatar: null,
        channel_account: 'shopify_whatsapp',
        status: 'issue',
        substatus: 'complaint',
        outcome: null,
        control_state: 'escalated',
        escalation_reason: 'refund_requested',
        channels: ['whatsapp', 'email'],
        last_message_snippet: 'The box arrived damaged and I need a replacement.',
        last_activity_at: '2026-07-01T09:18:00Z',
        needs_reply: true,
        next_send: null,
      },
      {
        lvl1_id: 45602,
        client_name: 'Ravi Patel',
        company: 'Order #A-1051',
        avatar: null,
        channel_account: 'shopify_whatsapp',
        status: 'processing',
        substatus: 'payment_pending',
        outcome: null,
        control_state: 'ai_running',
        escalation_reason: null,
        channels: ['whatsapp'],
        last_message_snippet: 'Payment link received, I will complete it today.',
        last_activity_at: '2026-07-01T08:51:00Z',
        needs_reply: false,
        next_send: { channel: 'whatsapp', sequence_step: 1, scheduled_at: '2026-07-01T12:00:00Z', preview: 'Sending a payment reminder if the order is still pending.' },
      },
      {
        lvl1_id: 45603,
        client_name: 'Northstar Ops',
        company: 'Order #A-1055',
        avatar: null,
        channel_account: 'support_email',
        status: 'fulfilled',
        substatus: 'shipped',
        outcome: null,
        control_state: 'ai_running',
        escalation_reason: null,
        channels: ['email'],
        last_message_snippet: 'Tracking number received. Thank you.',
        last_activity_at: '2026-07-01T08:05:00Z',
        needs_reply: false,
        next_send: null,
      },
      {
        lvl1_id: 45604,
        client_name: 'Elena Garcia',
        company: 'Order #A-1058',
        avatar: null,
        channel_account: 'support_email',
        status: 'new',
        substatus: 'order_placed',
        outcome: null,
        control_state: 'human_takeover',
        escalation_reason: null,
        channels: ['email', 'telegram'],
        last_message_snippet: 'Can you confirm the delivery address before shipping?',
        last_activity_at: '2026-06-30T18:42:00Z',
        needs_reply: false,
        next_send: { channel: 'email', sequence_step: 1, scheduled_at: '2026-07-01T13:30:00Z', preview: 'Confirming address and delivery window.' },
      },
    ],
  },
  threads: {
    '45601_whatsapp': {
      lvl1_id: 45601,
      channel: 'whatsapp',
      control_state: 'escalated',
      messages: [
        { id: 610, direction: 'inbound', state: 'received', body: 'The box arrived damaged and I need a replacement.', received_at: '2026-07-01T09:18:00Z' },
      ],
    },
    '45601_email': {
      lvl1_id: 45601,
      channel: 'email',
      control_state: 'escalated',
      messages: [
        { id: 611, direction: 'inbound', state: 'received', body: 'Please send return instructions for order A-1048.', received_at: '2026-07-01T09:20:00Z' },
      ],
    },
    '45602_whatsapp': {
      lvl1_id: 45602,
      channel: 'whatsapp',
      control_state: 'ai_running',
      messages: [
        { id: 620, direction: 'outbound', state: 'sent', sent_by: 'ai', body: 'Your payment link is ready. I can hold the order until noon.', sent_at: '2026-07-01T08:45:00Z' },
        { id: 621, direction: 'inbound', state: 'received', body: 'Payment link received, I will complete it today.', received_at: '2026-07-01T08:51:00Z' },
        { id: null, direction: 'outbound', state: 'scheduled', sent_by: 'ai', sequence_step: 1, body: 'Sending a payment reminder if the order is still pending.', scheduled_at: '2026-07-01T12:00:00Z', source: 'order_queue' },
      ],
    },
    '45603_email': {
      lvl1_id: 45603,
      channel: 'email',
      control_state: 'ai_running',
      messages: [
        { id: 630, direction: 'outbound', state: 'sent', sent_by: 'ai', body: 'Your order has shipped. Tracking is attached.', sent_at: '2026-07-01T07:55:00Z' },
        { id: 631, direction: 'inbound', state: 'received', body: 'Tracking number received. Thank you.', received_at: '2026-07-01T08:05:00Z' },
      ],
    },
    '45604_email': {
      lvl1_id: 45604,
      channel: 'email',
      control_state: 'human_takeover',
      messages: [
        { id: 640, direction: 'inbound', state: 'received', body: 'Can you confirm the delivery address before shipping?', received_at: '2026-06-30T18:42:00Z' },
        { id: null, direction: 'outbound', state: 'scheduled', sent_by: 'ai', sequence_step: 1, body: 'Confirming address and delivery window.', scheduled_at: '6-07-01T13:30:00Z', source: 'order_queue' },
      ],
    },
    '45604_telegram': {
      lvl1_id: 45604,
      channel: 'telegram',
      control_state: 'human_takeover',
      messages: [
        { id: 641, direction: 'inbound', state: 'received', body: 'Telegram is easier for delivery updates.', received_at: '2026-06-30T18:45:00Z' },
      ],
    },
  },
  suggestions: {
    '45601_whatsapp': [
      'I can arrange a replacement now. Please send one photo of the damaged box.',
      'I am sorry it arrived damaged. I can either replace it or start a refund.',
    ],
    '45604_email': [
      'Thanks for checking. Please confirm the full address below and I will release the shipment.',
      'I can hold shipping until the address is confirmed. Is this the correct delivery address?',
    ],
  },
};

export const createUniBoxApi = (dataset: UniBoxDataset): UniBoxApi => ({
  getFeed: () => Promise.resolve(dataset.feed),
  getThread: (lvl1_id, channel) =>
    Promise.resolve(dataset.threads[`${lvl1_id}_${channel}`] || { lvl1_id, channel, messages: [] }),
  getSuggestions: (lvl1_id, channel) => Promise.resolve(dataset.suggestions[`${lvl1_id}_${channel}`] || []),
});

export const salesApi = createUniBoxApi(salesDataset);
export const ordersApi = createUniBoxApi(ordersDataset);

/**
 * Map a dummy UniBox entity to a MappedLeadData-shaped object, so the existing
 * LeadReportModal can render the lead's full profile (incl. the Outreach tab)
 * from dummy data. previewMode disables LeadReportModal's live refetch, so we
 * must supply the whole object — this builds it from the entity + a realistic
 * profile snippet. Real wiring (next phase) replaces this with the backend.
 */
export const uniBoxEntityToMappedLead = (entity: UniBoxEntity): MappedLeadData => {
  const outreachSequences: OutreachStep[] = (entity.sequenceSteps || []).map((s) => ({
    step: s.step,
    channel: s.channel as OutreachStep['channel'],
    subject: s.subject,
    body: s.body,
    sentAt: s.sentAt,
  }));
  const industry = (entity.industry as string) || 'Technology';
  const role = (entity.lead_role as string) || 'Decision Maker';
  return {
    id: entity.lvl1_id,
    campaignId: String(entity.campaign_id || ''),
    status: entity.status,
    lastEnriched: new Date(entity.last_activity_at),
    leadName: entity.client_name,
    leadRole: role,
    leadLinkedin: entity.channels.includes('linkedin') ? `https://linkedin.com/in/${entity.lvl1_id}` : '',
    leadTwitter: null, leadFacebook: null, leadInstagram: null, leadYoutube: null, leadDiscord: null,
    leadLine: null, leadTiktok: null, leadWechat: null,
    leadWhatsapp: entity.channels.includes('whatsapp') ? '+1' : null,
    leadMessenger: null, leadGooglePlaces: null, leadTripadvisor: null,
    companyName: entity.company || '',
    companyNameFull: entity.company,
    companyWebsite: '', companyLinkedin: null, companyTwitter: null, companyFacebook: null,
    companyInstagram: null, companyYoutube: null, companyDiscord: null, companyLine: null,
    companyTiktok: null, companyWechat: null, companyWhatsapp: null, companyMessenger: null,
    companyGooglePlaces: null, companyTripadvisor: null,
    industry, companyIndustry: industry, companyEmployees: '', companySize: '', companyStage: '',
    revenueRange: '', companyCountry: '', companyCity: '', companyState: '', companyAddress: '',
    companyLat: null, companyLon: null, companyEmail: '', companyPhone: '', companyVat: null,
    fullAddress: '', companyFounded: '', companyLogo: '', businessModel: '',
    profilePicture: '', profilePictureStorageUrl: null,
    icpScore: 78, profileCompleteness: 80,
    yearsExperience: '', careerStage: '', professionalFocus: '', decisionAuthority: '',
    influenceLevel: '', budgetControl: '', linkedinFollowers: '',
    skills: [], keyProductsServices: [], keyResponsibilities: [],
    email: entity.channels.includes('email') ? `${entity.lvl1_id}@example.com` : '',
    email_score: 80, phone: '', mobile: '', language: 'English', languageCode: 'en',
    leadExecutiveOverview: `${entity.client_name} is the ${role} at ${entity.company || 'their company'}, leading initiatives in ${industry.toLowerCase()}.`,
    companyExecutiveOverview: `${entity.company || 'The company'} operates in the ${industry} sector.`,
    companyGeneratorSummary: '', generatorSummary: '', linkedinSummary: '', researchSummary: null,
    opportunities: [], painPoints: [], communicationStyle: '', decisionStyle: '', riskTolerance: '',
    targetAudience: null, differentiationFactors: [],
    outreachSequences,
    agentName: 'Brainiacs AI', agentPicture: null,
    interactions: [], recentActivities: [],
    rawCompanySummary: null,
    rawProfileIntel: {
      temporal_intelligence: { timezone: entity.timezone || 'UTC', optimal_send_times: [{ day: 'Tuesday', time: '10:15', rationale: 'Mid-morning post-triage focus time.' }] },
      engagement_framework: { communication_protocol: 'Direct, evidence-led; cite a concrete pain + a low-friction CTA.' },
      communication_preferences: { primary_language: 'English' },
      psychological_profile: { buyer_archetype: { type: 'Early_Majority', buying_motivation: 'Risk_minimization' } },
      social_intelligence: { icebreaker_intelligence: { tier_2_hooks: [{ hook_type: 'company_context', opening_line: outreachSequences[0]?.body || '', transition: 'a short call this week' }], conversation_starters: ['How are you measuring the impact today?'] } },
    },
    rawLeadSummary: null, rawOriginalData: null,
    personLvl2: null, personLvl1: null, companyLvl1: null,
    personLvl2Overview: null, personLvl2PersonalizationStrategy: null, companySummary: null, profileIntelligence: null,
    isPlaceholder: false,
  } as unknown as MappedLeadData;
};
